package com.videoanalyzer.web.components.auth

import androidx.compose.runtime.*
import com.videoanalyzer.web.components.Logo
import com.videoanalyzer.web.components.LogoSize
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Shared components for auth pages.
 */

@Composable
fun AuthPageLayout(
    title: String,
    subtitle: String,
    onBack: () -> Unit,
    content: @Composable () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(100.vh)
            padding(24.px)
        }
    }) {
        Div({
            classes("card", "scale-in")
            style {
                maxWidth(440.px)
                width(100.percent)
                textAlign("center")
                padding(40.px)
            }
        }) {
            // Back button
            Div({
                style {
                    position(Position.Absolute)
                    top(20.px)
                    left(20.px)
                }
            }) {
                Button({
                    style {
                        backgroundColor(Color.transparent)
                        border(0.px)
                        color(Color("#94a3b8"))
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        fontSize(14.px)
                        padding(8.px)
                        borderRadius(8.px)
                        property("transition", "all 0.2s ease")
                    }
                    onClick { onBack() }
                }) {
                    Span { Text("←") }
                    Text("Back")
                }
            }

            // Logo
            Div({
                style { marginBottom(20.px) }
            }) {
                Logo(size = LogoSize.LARGE, animate = true)
            }

            // Title
            H1({
                classes("gradient-text")
                style {
                    fontSize(28.px)
                    fontWeight(800)
                    marginBottom(8.px)
                }
            }) { Text(title) }

            // Subtitle
            P({
                style {
                    color(Color("#94a3b8"))
                    marginBottom(28.px)
                    fontSize(15.px)
                }
            }) { Text(subtitle) }

            content()
        }
    }
}

@Composable
fun LegalPageLayout(
    title: String,
    onBack: () -> Unit,
    content: @Composable () -> Unit
) {
    Div({
        style {
            minHeight(100.vh)
            padding(24.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        Div({
            style {
                maxWidth(800.px)
                width(100.percent)
            }
        }) {
            // Header with back button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    marginBottom(32.px)
                    paddingTop(20.px)
                }
            }) {
                Button({
                    style {
                        backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                        border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.1)"))
                        borderRadius(8.px)
                        padding(10.px, 16.px)
                        color(Color("#94a3b8"))
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        fontSize(14.px)
                    }
                    onClick { onBack() }
                }) {
                    Text("← Back")
                }

                H1({
                    style {
                        fontSize(28.px)
                        fontWeight(700)
                        color(Color("#f8fafc"))
                        margin(0.px)
                    }
                }) { Text(title) }
            }

            // Content card
            Div({
                classes("card")
                style {
                    padding(40.px)
                    textAlign("left")
                }
            }) {
                content()
            }
        }
    }
}

@Composable
fun DisplayMessages(error: String?, success: String?) {
    if (error != null) {
        Div({
            classes("fade-in")
            style {
                backgroundColor(Color("rgba(239, 68, 68, 0.15)"))
                border(1.px, LineStyle.Solid, Color("rgba(239, 68, 68, 0.4)"))
                borderRadius(12.px)
                padding(14.px)
                marginBottom(20.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            Span({ style { fontSize(18.px) } }) { Text("⚠️") }
            P({
                style {
                    color(Color("#fca5a5"))
                    fontSize(14.px)
                    margin(0.px)
                    textAlign("left")
                }
            }) { Text(error) }
        }
    }

    if (success != null) {
        Div({
            classes("fade-in")
            style {
                backgroundColor(Color("rgba(16, 185, 129, 0.15)"))
                border(1.px, LineStyle.Solid, Color("rgba(16, 185, 129, 0.4)"))
                borderRadius(12.px)
                padding(14.px)
                marginBottom(20.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            Span({ style { fontSize(18.px) } }) { Text("✅") }
            P({
                style {
                    color(Color("#34d399"))
                    fontSize(14.px)
                    margin(0.px)
                    textAlign("left")
                }
            }) { Text(success) }
        }
    }
}

@Composable
fun SocialLoginButtons(
    onGoogle: () -> Unit,
    isLoading: Boolean
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            marginBottom(24.px)
        }
    }) {
        GoogleSignInButton(onClick = onGoogle, enabled = !isLoading)
    }
}

@Composable
fun GoogleSignInButton(onClick: () -> Unit, enabled: Boolean) {
    Button({
        style {
            width(100.percent)
            padding(14.px, 24.px)
            backgroundColor(Color("rgba(255, 255, 255, 0.95)"))
            color(Color("#1f1f1f"))
            border(1.px, LineStyle.Solid, Color("rgba(0, 0, 0, 0.1)"))
            borderRadius(12.px)
            fontSize(14.px)
            fontWeight(500)
            cursor(if (enabled) "pointer" else "not-allowed")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(12.px)
            opacity(if (enabled) 1.0 else 0.6)
            property("transition", "all 0.2s ease")
        }
        onClick { if (enabled) onClick() }
        if (!enabled) disabled()
    }) {
        GoogleLogo()
        Span { Text("Continue with Google") }
    }
}

@Composable
fun GoogleLogo() {
    var divRef by remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }

    DisposableEffect(divRef) {
        divRef?.innerHTML = """
            <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
        """.trimIndent()
        onDispose { }
    }

    Div({
        style {
            width(20.px)
            height(20.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
        ref { element ->
            divRef = element
            onDispose { divRef = null }
        }
    })
}

@Composable
fun AuthDivider() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            marginBottom(24.px)
            gap(16.px)
        }
    }) {
        Hr({
            style {
                property("flex", "1")
                border(0.px)
                height(1.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
            }
        })
        Span({
            style {
                color(Color("#64748b"))
                fontSize(13.px)
                fontWeight(500)
            }
        }) { Text("or continue with email") }
        Hr({
            style {
                property("flex", "1")
                border(0.px)
                height(1.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
            }
        })
    }
}

@Composable
fun EmailPasswordForm(
    email: String,
    onEmailChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    isLoading: Boolean,
    buttonText: String,
    onSubmit: () -> Unit,
    onForgotPassword: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
        }
    }) {
        FormField("Email") {
            Input(InputType.Email) {
                classes("input")
                value(email)
                onInput { onEmailChange(it.value) }
                attr("placeholder", "you@example.com")
                style {
                    width(100.percent)
                    padding(14.px, 16.px)
                    fontSize(15.px)
                }
                if (isLoading) disabled()
            }
        }

        Div {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(8.px)
                }
            }) {
                Span({
                    style {
                        color(Color("#94a3b8"))
                        fontSize(13.px)
                        fontWeight(500)
                    }
                }) { Text("Password") }

                if (onForgotPassword != null) {
                    Span({
                        style {
                            color(Color("#0EA5E9"))
                            fontSize(13.px)
                            cursor("pointer")
                            fontWeight(500)
                        }
                        onClick { onForgotPassword() }
                    }) { Text("Forgot password?") }
                }
            }

            Input(InputType.Password) {
                classes("input")
                value(password)
                onInput { onPasswordChange(it.value) }
                attr("placeholder", "••••••••")
                style {
                    width(100.percent)
                    padding(14.px, 16.px)
                    fontSize(15.px)
                }
                if (isLoading) disabled()
            }
        }

        Button({
            classes("btn", "btn-primary", "btn-large")
            style {
                width(100.percent)
                marginTop(8.px)
                backgroundColor(Color("#E53935"))
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.35)")
            }
            onClick { onSubmit() }
            if (isLoading) disabled()
        }) {
            if (isLoading) {
                LoadingButtonContent()
            } else {
                Text(buttonText)
            }
        }
    }
}

@Composable
fun LoadingButtonContent() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(8.px)
        }
    }) {
        Div({
            classes("spinner")
            style {
                width(18.px)
                height(18.px)
                property("border-width", "2px")
            }
        })
        Text("Please wait...")
    }
}

@Composable
fun AuthSwitch(text: String, linkText: String, onClick: () -> Unit) {
    P({
        style {
            marginTop(24.px)
            fontSize(14.px)
            color(Color("#94a3b8"))
        }
    }) {
        Text("$text ")
        Span({
            style {
                color(Color("#0EA5E9"))
                cursor("pointer")
                fontWeight(600)
            }
            onClick { onClick() }
        }) { Text(linkText) }
    }
}

@Composable
fun FormField(label: String, content: @Composable () -> Unit) {
    Div({
        style { textAlign("left") }
    }) {
        Span({
            style {
                display(DisplayStyle.Block)
                color(Color("#94a3b8"))
                fontSize(13.px)
                fontWeight(500)
                marginBottom(8.px)
            }
        }) { Text(label) }
        content()
    }
}

@Composable
fun TermsLinks(
    onTermsOfService: () -> Unit,
    onPrivacyPolicy: () -> Unit,
    showAgreement: Boolean = false
) {
    P({
        style {
            marginTop(24.px)
            fontSize(12.px)
            color(Color("#64748b"))
            lineHeight("1.6")
        }
    }) {
        if (showAgreement) {
            Text("By creating an account, you agree to our ")
        } else {
            Text("By signing in, you agree to our ")
        }
        Span({
            style {
                color(Color("#0EA5E9"))
                cursor("pointer")
                property("text-decoration", "underline")
            }
            onClick { onTermsOfService() }
        }) { Text("Terms of Service") }
        Text(" and ")
        Span({
            style {
                color(Color("#0EA5E9"))
                cursor("pointer")
                property("text-decoration", "underline")
            }
            onClick { onPrivacyPolicy() }
        }) { Text("Privacy Policy") }
        Text(".")
    }
}

@Composable
fun LegalSection(title: String, content: @Composable () -> Unit) {
    Div({
        style {
            marginBottom(28.px)
        }
    }) {
        H3({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(Color("#f8fafc"))
                marginBottom(12.px)
            }
        }) { Text(title) }

        Div({
            style {
                fontSize(14.px)
                color(Color("#94a3b8"))
                lineHeight("1.7")
            }
        }) {
            content()
        }
    }
}

@Composable
fun FooterLink(text: String, onClick: () -> Unit) {
    Span({
        style {
            color(Color("#64748b"))
            fontSize(13.px)
            cursor("pointer")
            property("transition", "color 0.2s ease")
        }
        onClick { onClick() }
    }) { Text(text) }
}
